// İSG Portal — Supplier İş İzni (web-supplier — taşeron Yetkili) API çağrıları + tipleri.
// **Taşeron principal tarafı** (supplier login §5.4 sonrası). Taşeron Yetkilisi aktif
// firmasındaki **kendi contractor'ının** iş izinlerini görür (salt-okunur — İSG uzmanı §14'te
// izni açar/yönetir) ve o izinlere **kendi personelini** atar/çıkarır + her personelin eğitim
// önkoşul (readiness) durumunu görür.
//
// KAYNAK: api-contract.md §21 (Supplier İş İzni — `[ModuleAction("supplier-contractor", …)]`).
// Backend kanonu; burada **varsayım YOK** — endpoint yolları, istek/yanıt alanları ve kimlik
// kuralları (Result unwrap `value`; liste → `value` + `total`/`filteredTotal`) doğrudan
// sözleşmedendir. Serileştirme camelCase (§1).
//
// **Scope izolasyonu (KRİTİK — §21).** Firma/contractor kimliği **istemciden ASLA alınmaz** —
// aktif firmadaki `SupplierUserCompany.ContractorId` bağından + SupplierPermitScope ile sunucu
// çözer (§15 yol B `SupplierContractorScope` aynası). Yetkili **yalnız kendi contractor'ının**
// izinlerine + personeline erişir; başka firma/contractor verisi hiçbir koşulda dönmez/atanmaz
// (yok gibi `404`). İstemci bu yüzden hiçbir uçta firma/contractor kimliği GÖNDERMEZ.
//
// **İki-katman yetki.** Tüm uçlar `supplier-contractor` modülündedir — yalnız Taşeron
// **Yetkilisi** (web portalı grant'lı) geçer; **Personeli** (`supplier-self`) 403 alır. İzin
// bilgileri supplier tarafında salt-okunurdur (İSG uzmanı yönetir); Yetkili yalnız personel
// atar/çıkarır. `Status`/`HazardClass` + readiness DTO'ları §14 kanonuyla ORTAK (permits.ts) —
// yeniden tanımlanmaz, import edilir. `permitTypeName` firma-tanımlı tip adı (§22; eski `workType`).

import { api } from './client';
import type { HazardClass } from './contractors';
import type { Empty } from './envelope';
import type {
  PermitStatus,
  RequiredTrainingDto,
  VehicleWriteRequest,
  WorkPermitPersonnelDto,
  WorkPermitSiteDto,
  WorkPermitVehicleDto,
} from './permits';
import type { ProtectiveEquipmentRefDto } from './protectiveEquipment';

/**
 * Supplier iş izni **liste satırı** — api-contract §21 (`SupplierPermitDto`). Giriş yapan
 * Yetkili'nin **kendi contractor'ının** bir izni; taşeron **implicit**'tir (daima kendi
 * contractor'ı) → `contractorUuid`/`contractorName` TAŞIMAZ, onaylayan (İSG uzmanı) kimliği de
 * supplier'a gösterilmez. `permitTypeName` iznin firma-tanımlı tipinin adı (§22; eski `workType`
 * yerine; soft-delete edilmiş tip adı da çözülür). `sites` = iznin kapsadığı sahalar (**çoklu
 * lokasyon — §14 Faz 2**; eski tekil `siteUuid?`/`siteName?` yerine; ada göre sıralı, `[]` olabilir).
 * `status` okuma anında türetilir (`validityEnd` geçmiş Approved/Active → `Expired`).
 * `readyPersonnelCount` = kapsanan personelden **tüm gerekli eğitimleri geçmiş** (readiness=true;
 * §17) olanların sayısı; gerekli eğitim yoksa kapsanan herkes trivially hazırdır.
 * `validityStart`/`validityEnd`/`createdAt` UTC ISO (§4; DateTime).
 */
export interface SupplierPermitDto {
  uuid: string;
  /** İznin firma-tanımlı tipinin adı (§22; soft-delete edilmiş tip için de çözülür). */
  permitTypeName: string | null;
  title: string;
  /** İznin kapsadığı sahalar — ada göre sıralı; `[]` olabilir (**çoklu lokasyon — §14 Faz 2**). */
  sites: WorkPermitSiteDto[];
  hazardClass: HazardClass;
  /** Geçerlilik başlangıcı — UTC ISO (§4; DateTime). */
  validityStart: string;
  /** Geçerlilik bitişi — UTC ISO (§4; DateTime). Türetilmiş `Expired`'ın kaynağı. */
  validityEnd: string;
  status: PermitStatus;
  personnelCount: number;
  /** Tüm gerekli eğitimleri geçmiş (hazır) personel sayısı (§17 türetimi). */
  readyPersonnelCount: number | null;
  createdAt: string | null;
}

/**
 * Supplier iş izni **detay** görünümü — api-contract §21 (`SupplierPermitDetailDto`,
 * `GET /supplier/permits/{uuid}`). İzin alanları + iznin **gerekli eğitimleri** (§17 önkoşul) +
 * iznin permit tipinde **istenen belge türleri** (`requiredDocuments`; §22 `PermitTypeRequiredDocument`
 * `entity = personnel`; §27 kayıt kodları, kayıt sırası §27.0(5); etiket §27 kaydından; istenen
 * belge yoksa `[]`) + iznin kapsadığı **personel** (her biri belge + eğitim önkoşul durumuyla — kanonik
 * `WorkPermitPersonnelDto`; `requiredDocumentsSatisfied`/`missingDocumentTypes` + eğitim + `ready` dahil).
 * Yetkili buradan izinde **ne istendiğini** + hangi personelin **neyi eksik** olduğunu görür (supplier
 * burada belge YÜKLEMEZ — yalnız görünürlük). Yalnız kendi contractor'ının izni (yabancı →
 * `404 PERMIT_NOT_FOUND`).
 */
export interface SupplierPermitDetailDto {
  permit: SupplierPermitDto;
  requiredTrainings: RequiredTrainingDto[];
  /** İznin tipinde istenen belge türleri — §27 `personnel` kayıt kodları (§22). */
  requiredDocuments: string[];
  /**
   * İznin permit tipinde istenen **KKD (Kişisel Koruyucu Donanım)** listesi — `[{ uuid, name }]`
   * (api-contract §24.3; `ProtectiveEquipmentRefDto` §24). Taşeron **read-only** görür (donanımı
   * taşeron getirir); tipe KKD bağlanmamış izin → `[]`. Kontrol itemları (§25) supplier DTO'sunda
   * **YOK** — İSG uzmanı iç checklist'idir (plan D4; backend supplier projeksiyonuna dahil değil).
   */
  requiredProtectiveEquipment: ProtectiveEquipmentRefDto[];
  personnel: WorkPermitPersonnelDto[];
  /**
   * İzinde araç olacak mı? — "araç olacak mı?" form bayrağı (§23 Faz 3a). `true` iken Yetkili
   * araç ekler + evrak yükler; araç bölümü buna göre gösterilir. **Detay nesnesinin kendi
   * alanı** (nested `permit` DEĞİL — BE `SupplierPermitDetailDto` üst düzeyi).
   */
  hasVehicles: boolean;
  /**
   * İznin permit tipinin istediği **araç** belge türleri — §27 `vehicle` kayıt kodları
   * (§22 `entity = vehicle`; kayıt sırası §27.0(5); etiket §27 kaydından; tipte
   * istenen araç belgesi yoksa `[]`; §23). Yetkili her araçta bunların karşılanmasını görür.
   */
  requiredVehicleDocuments: string[];
  /**
   * İznin araçları (§23) — her biri evrak seti + `vehicleReady` + `missingVehicleDocumentTypes`
   * ile. **Personel giriş-gate'i AYNEN kalır** — araç ayrı göstergedir. Yükleme/silme sonrası
   * bu liste `GET /supplier/permits/{uuid}` invalidate ile tazelenir (readiness sunucu otoritesi).
   */
  vehicles: WorkPermitVehicleDto[];
}

/**
 * Supplier izin liste/filtre parametreleri — api-contract §21 (`GET /supplier/permits`) + §3
 * paging. `status` (türetilmiş `Expired` dahil; case-insensitive), `search` (başlık substring).
 * Sıra backend'de geçerlilik başlangıcı azalan (istemci sort göndermez).
 */
export interface SupplierPermitListParams {
  status?: PermitStatus;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Personel atama isteği — api-contract §21.2 (`POST /supplier/permits/{uuid}/personnel`).
 * `supplierUserUuids` = **kendi contractor'ına** + aktif firmaya atanmış `SupplierUser` public
 * kimlikleri (herhangi biri değilse `404 PERSONNEL_NOT_FOUND` — kısmi yazma yok). Boş →
 * `400 VALIDATION_FAILED`. Zaten kapsanan personel idempotent atlanır.
 */
export interface AssignSupplierPersonnelRequest {
  supplierUserUuids: string[];
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `GET /supplier/permits` (`get`) — Yetkili'nin **kendi contractor'ının** izinleri (sayfalı +
 * filtreli). Sıra geçerlilik başlangıcı azalan; `Expired` durumu backend'de türetilir. Firma/
 * contractor kimliği GÖNDERİLMEZ — sunucu bağdan çözer. Bağ yoksa → `404 CONTRACTOR_NOT_FOUND`;
 * aktif firmaya atama yoksa → `403 COMPANY_NOT_ACCESSIBLE`.
 */
export function listSupplierPermits(params: SupplierPermitListParams = {}) {
  return api.list<SupplierPermitDto>('supplier/permits', {
    searchParams: {
      status: params.status,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /supplier/permits/{uuid}` (`get`) — tek izin + gerekli eğitimler + kapsanan personel
 * (readiness ile). Yok / başka contractor / başka firma → `404 PERMIT_NOT_FOUND`.
 */
export function getSupplierPermit(uuid: string) {
  return api.get<SupplierPermitDetailDto>(`supplier/permits/${uuid}`);
}

/**
 * `POST /supplier/permits/{uuid}/personnel` (`update`) — izne **kendi contractor'ının** personelini
 * atar (idempotent; her `supplierUserUuid` kendi contractor'ına + aktif firmaya atanmış olmalı —
 * değilse `404 PERSONNEL_NOT_FOUND`, kısmi yazma yok). Boş liste → `400 VALIDATION_FAILED`; izin
 * yok/kapsam dışı → `404 PERMIT_NOT_FOUND`. → güncel personel listesi (readiness'lı).
 */
export function assignSupplierPermitPersonnel(uuid: string, body: AssignSupplierPersonnelRequest) {
  return api.post<WorkPermitPersonnelDto[]>(`supplier/permits/${uuid}/personnel`, { json: body });
}

/**
 * `DELETE /supplier/permits/{uuid}/personnel/{userUuid}` (`update`) — personeli iznin kapsamından
 * çıkarır (soft-delete bağ; `userUuid` = `SupplierUser` public kimliği). İzin yok →
 * `404 PERMIT_NOT_FOUND`; personel kapsamda değil → `404 PERSONNEL_NOT_FOUND`.
 */
export function removeSupplierPermitPersonnel(uuid: string, userUuid: string) {
  return api.delete<Empty>(`supplier/permits/${uuid}/personnel/${userUuid}`);
}

// ============================================================================
// 23.4 İzin araçları — BAĞ (taşeron Yetkili) — api-contract §23.4
// ============================================================================
//
// Araç kaydı + belge yükleme/silme `supplierContractorApi`'dedir (`/supplier/contractor/vehicles…`,
// §23.2). Burada yalnız izin–araç bağı vardır. `vehicleUuid` = KAYIT aracı (§23.0.1). Eski
// `…/permits/{uuid}/vehicles/{vid}` DELETE ve `…/documents` uçları kalktı (§23.5).

/**
 * `POST /supplier/permits/{uuid}/vehicles` (`update`) — kendi taşeronunun iznine araç bağlar
 * (reuse-or-create by plate key; §23.3 ile aynı). Yeni bağ `201`, zaten bağlı `200`.
 */
export function addSupplierPermitVehicle(uuid: string, body: VehicleWriteRequest) {
  return api.post<WorkPermitVehicleDto>(`supplier/permits/${uuid}/vehicles`, { json: body });
}

/**
 * `DELETE /supplier/permits/{uuid}/vehicles/{vehicleUuid}` (`update`) — aracı izinden ÇIKARIR
 * (yalnız bağ; kayıt aracı ve belgeleri kalır). Bağlı değil → `404 VEHICLE_NOT_FOUND`.
 */
export function removeSupplierPermitVehicle(uuid: string, vehicleUuid: string) {
  return api.delete<Empty>(`supplier/permits/${uuid}/vehicles/${vehicleUuid}`);
}
